// src/app/shared/toast/toast.service.ts
import { Injectable, signal, computed } from '@angular/core';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  title?: string;
  duration?: number; // ms
}

export interface Toast {
  id: number;
  message: string;
  title?: string;
  variant: ToastVariant;
  createdAt: number;
  duration: number;
}

let _id = 0;

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly DEFAULT_DURATION = 5000;
  private _toasts = signal<Toast[]>([]);
  readonly toasts = computed(() => this._toasts());

  private push(variant: ToastVariant, message: string, opts?: ToastOptions) {
    const id = ++_id;
    const t: Toast = {
      id,
      message,
      title: opts?.title,
      variant,
      createdAt: Date.now(),
      duration: opts?.duration ?? this.DEFAULT_DURATION,
    };
    this._toasts.update((list) => [t, ...list]);
    if (t.duration > 0) {
      setTimeout(() => this.dismiss(id), t.duration);
    }
    return id;
  }

  success(msg: string, opts?: ToastOptions) {
    return this.push('success', msg, opts);
  }
  error(msg: string, opts?: ToastOptions) {
    return this.push('error', msg, opts);
  }
  info(msg: string, opts?: ToastOptions) {
    return this.push('info', msg, opts);
  }
  warning(msg: string, opts?: ToastOptions) {
    return this.push('warning', msg, opts);
  }

  dismiss(id: number) {
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  clear() {
    this._toasts.set([]);
  }
}
